import React from 'react';
import { useToast } from '@askporter/toast';
import { useMaintenanceCopy } from '../MaintenanceBanner/useMaintenanceCopy';

/**
 * Raises a toast the moment a write is refused by a maintenance window.
 *
 * Renders nothing. Mount it once, inside a `ToastProvider`.
 *
 * **Why this exists separately from the banner.** Write controls stay enabled
 * during a window by deliberate decision (NS-2461, 11 Aug 2026), so the user
 * finds out their change was refused only after attempting it. The banner
 * carries the standing account of the window, but it sits at the top of the
 * page and the user may be far down a long form when the refusal lands — so on
 * its own it fails the criterion that the refusal is surfaced *where the user is
 * looking*. Before this, a refused write in core-ui produced nothing at the
 * point of action.
 *
 * Keyed on `detectedAt` rather than on the refusal object, so a re-render cannot
 * re-toast the same refusal, while a second refused write in the same window
 * still gets its own toast.
 *
 * `warning` rather than `error`: nothing has broken, and the platform is doing
 * what it was told to. Copy is shared with the banner via
 * {@link useMaintenanceCopy}, so the two cannot describe the same window
 * differently.
 */
export const MaintenanceRefusalToast: React.FC = () => {
  const { refusal, title, refusalMessage } = useMaintenanceCopy();
  const { setToast } = useToast();

  const lastToasted = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (!refusal) {
      // Cleared with the window, so the next window's first refusal toasts even
      // if it happens to carry the same timestamp.
      lastToasted.current = null;
      return;
    }

    if (lastToasted.current === refusal.detectedAt) return;
    lastToasted.current = refusal.detectedAt;

    setToast({ severity: 'warning', title, subtitle: refusalMessage });
  }, [refusal, title, refusalMessage, setToast]);

  return null;
};
